import { useState, cloneElement } from 'react'
import PropTypes from 'prop-types'
import {
  useFloating,
  useHover,
  useInteractions,
  useRole,
  useDismiss,
  useTransitionStyles,
  offset,
  flip,
  shift,
  safePolygon,
  FloatingPortal,
} from '@floating-ui/react'
import styles from './Tooltip.module.css'

/* La clase --top/--bottom/--left/--right del DS describe hacia dónde apunta la FLECHA,
   no dónde queda la caja. Si la caja queda arriba del trigger, la flecha apunta hacia
   abajo -> esa es la clase --bottom del DS (y viceversa). Se invierte acá para que la
   prop `placement` de este componente use el sentido intuitivo (placement="top" =
   la caja aparece arriba), que es como funciona floating-ui y el resto del ecosistema. */
const ARROW_SIDE = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' }

export function Tooltip({
  title,
  description,
  paginator,
  action,
  placement = 'top',
  dismissible = true,
  children,
}) {
  const [open, setOpen] = useState(false)

  const { refs, floatingStyles, context, placement: finalPlacement } = useFloating({
    open,
    onOpenChange: setOpen,
    placement,
    middleware: [offset(8), flip(), shift({ padding: 8 })],
  })

  const hover = useHover(context, { move: false, handleClose: safePolygon() })
  const dismiss = useDismiss(context)
  const role = useRole(context, { role: 'tooltip' })

  const { getReferenceProps, getFloatingProps } = useInteractions([hover, dismiss, role])

  const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
    duration: 200,
    initial: { opacity: 0 },
  })

  const boxSide = finalPlacement.split('-')[0]
  const arrowSide = ARROW_SIDE[boxSide]
  const arrow = <div className={styles.tooltip__arrow} />

  return (
    <>
      {cloneElement(children, {
        ref: refs.setReference,
        ...getReferenceProps(children.props),
      })}
      {isMounted && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={{ ...floatingStyles, ...transitionStyles }}
            className={[styles.tooltip, styles[`tooltip--${arrowSide}`]].filter(Boolean).join(' ')}
            {...getFloatingProps()}
          >
            {(arrowSide === 'left' || arrowSide === 'top') && arrow}
            <div className={styles.tooltip__box}>
              <div className={styles.tooltip__header}>
                <div className={styles.tooltip__body}>
                  {title && <span className={styles.tooltip__title}>{title}</span>}
                  {description && <span className={styles.tooltip__desc}>{description}</span>}
                </div>
                {dismissible && (
                  <button
                    type="button"
                    className={styles.tooltip__close}
                    onClick={() => setOpen(false)}
                    aria-label="Cerrar"
                  >
                    <span className="material-icons-outlined">close</span>
                  </button>
                )}
              </div>
              {(paginator || action) && (
                <div className={styles.tooltip__footer}>
                  {paginator && <span className={styles.tooltip__paginator}>{paginator}</span>}
                  {action && <span className={styles.tooltip__action}>{action}</span>}
                </div>
              )}
            </div>
            {(arrowSide === 'right' || arrowSide === 'bottom') && arrow}
          </div>
        </FloatingPortal>
      )}
    </>
  )
}

Tooltip.propTypes = {
  children: PropTypes.element.isRequired,
  title: PropTypes.node,
  description: PropTypes.node,
  paginator: PropTypes.node,
  action: PropTypes.node,
  placement: PropTypes.oneOf(['top', 'bottom', 'left', 'right']),
  dismissible: PropTypes.bool,
}
